import { useEffect, useState } from "react";

/**
 * useGeoPermission — light hook that reports the browser's geolocation
 * permission state. Values:
 *   • "granted"   — GPS is available, buttons work normally
 *   • "denied"    — user hard-blocked location (show enable-instructions)
 *   • "prompt"    — never asked yet (show "Enable GPS" CTA)
 *   • "unsupported" — old browser / non-secure origin
 *
 * We poll `navigator.permissions` and also listen for its `change` event,
 * so if the worker fixes their setting in another tab we pick it up
 * immediately. If Permissions API isn't there (Safari <13, some
 * webviews), we probe by calling getCurrentPosition once and inferring
 * from the outcome.
 */
export function useGeoPermission() {
  const [state, setState] = useState("prompt");

  useEffect(() => {
    let mounted = true;
    let statusRef = null;

    async function probe() {
      if (typeof navigator === "undefined") return;
      if (!("geolocation" in navigator)) {
        if (mounted) setState("unsupported");
        return;
      }
      // Prefer Permissions API — no side-effect prompt
      if (navigator.permissions && navigator.permissions.query) {
        try {
          statusRef = await navigator.permissions.query({ name: "geolocation" });
          if (!mounted) return;
          setState(statusRef.state);
          statusRef.onchange = () => { if (mounted) setState(statusRef.state); };
          return;
        } catch { /* fall through to inference */ }
      }
      // Fallback: infer via getCurrentPosition with a tiny timeout. If
      // it errors with PERMISSION_DENIED we know it's blocked; otherwise
      // we optimistically say "prompt" so the CTA renders.
      navigator.geolocation.getCurrentPosition(
        () => mounted && setState("granted"),
        (err) => {
          if (!mounted) return;
          if (err && err.code === 1) setState("denied");   // PERMISSION_DENIED
          else setState("prompt");
        },
        { maximumAge: 60_000, timeout: 800 },
      );
    }

    probe();
    return () => {
      mounted = false;
      if (statusRef) statusRef.onchange = null;
    };
  }, []);

  return state;
}

/**
 * Small helper — prompt the user for location and resolve to the
 * updated permission state. Useful for "Enable GPS" buttons.
 */
export function requestGeoPermission() {
  return new Promise((resolve) => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      return resolve("unsupported");
    }
    navigator.geolocation.getCurrentPosition(
      () => resolve("granted"),
      (err) => resolve(err && err.code === 1 ? "denied" : "prompt"),
      { maximumAge: 0, timeout: 10_000, enableHighAccuracy: true },
    );
  });
}
